import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { OccupancyKind } from '@immoaktas/shared';

/**
 * Status system of a flat (design 1-03, tokens color.unitStatus): fill + solid outline = somebody lives there, white +
 * dashed = nobody, a band at the bottom = it changes soon (yellow move-out, blue move-in, green changeover), red only
 * for money. Never colour alone: outline style, band and the arrears dot carry the state too, every tile has text.
 */

export type BandKind = 'move_out' | 'move_in' | 'change';

/** Tile of the small house drawing: label, one or two lines, band with date, money pill. */
export function OccupancyTile({ to, kind, title, lines, band, bandKind, pill, arrears, ariaLabel, selected, onClick, footer }: {
  to?: string;
  kind: OccupancyKind;
  title: ReactNode;
  lines?: ReactNode[];
  band?: ReactNode;
  bandKind?: BandKind;
  /** Red pill top right („kostet dich“, „−1.400 €“). */
  pill?: ReactNode;
  arrears?: boolean;
  ariaLabel?: string;
  selected?: boolean;
  onClick?: () => void;
  /** One extra short line (meter status, lock reason). */
  footer?: ReactNode;
}) {
  const cls = ['occ-tile', `oc-${kind}`, band ? 'has-band' : '', selected ? 'is-selected' : ''].filter(Boolean).join(' ');
  const body = (
    <>
      <span className="ot-head">
        <span className="ot-title">{title}</span>
        {pill != null && <span className="ot-pill">{pill}</span>}
        {arrears && pill == null && <span className="occ-dot" aria-hidden />}
      </span>
      {lines?.filter((l) => l != null && l !== '').map((l, i) => <span key={i} className={i === 0 ? 'ot-line' : 'ot-sub'}>{l}</span>)}
      {footer != null && <span className="ot-extra">{footer}</span>}
      {band && <span className={`ot-band b-${bandKind ?? 'move_out'}`}>{band}</span>}
    </>
  );
  if (to) return <Link to={to} className={cls} aria-label={ariaLabel} onClick={onClick}>{body}</Link>;
  if (onClick) return <button type="button" className={cls} aria-label={ariaLabel} aria-pressed={selected} onClick={onClick}>{body}</button>;
  return <div className={cls} aria-label={ariaLabel}>{body}</div>;
}

/** Status word as a pill (list rows, unit header, detail panel): same colours as the tile. */
export function OccupancyPill({ kind, children, band }: { kind: OccupancyKind; children: ReactNode; band?: BandKind }) {
  return <span className={`occ-pill oc-${kind}${band ? ` b-${band}` : ''}`}>{children}</span>;
}

/** Stacked bar of the house: one segment per state in a fixed order; `title` carries the numbers for hover. */
export function OccupancyBar({ segments, label }: { segments: { kind: OccupancyKind; count: number; label: string }[]; label: string }) {
  const total = segments.reduce((s, x) => s + x.count, 0) || 1;
  return (
    <span className="occ-bar" role="img" aria-label={label}>
      {segments.map((s) => <span key={s.kind} className={`ob-seg oc-${s.kind}`} style={{ flexGrow: s.count, flexBasis: `${(s.count / total) * 100}%` }} title={`${s.label}: ${s.count}`} />)}
    </span>
  );
}

export type PlanCell = {
  id: string;
  /** Short text in the field („19.08“). */
  label: string;
  /** Full accessible name („WE 19.08, zieht aus am 30.11., Rückstand 420 €“). */
  name: string;
  kind: OccupancyKind;
  band?: BandKind | null;
  arrears?: boolean;
  /** Filtered out: stays in place (seat plan), greyed and skipped by the keyboard. */
  dim?: boolean;
  /** Empty seat (column groups: a stairwell without a flat on this floor); not focusable. */
  gap?: boolean;
};
export type PlanFloor = { id: string; label: string; hint?: ReactNode; cells: PlanCell[] };
export type PlanFold = { id: string; label: ReactNode; text: ReactNode; onExpand: () => void; expandLabel: string };
export type PlanRowItem = { kind: 'floor'; floor: PlanFloor } | { kind: 'fold'; fold: PlanFold };

/**
 * Seat plan of a big house (design 3-11): one fixed grid, one field per flat with its number, floor column left,
 * folded quiet floors as one row. More than `columns` flats per floor wrap into a second line instead of scrolling.
 * Keyboard: arrows walk the fields (up/down keep the column), Enter opens, Space or click selects.
 */
export function HousePlan({ rows, columns, selected, onSelect, onOpen, label, header, columnGroups }: {
  rows: PlanRowItem[];
  columns: number;
  /** Column groups over the cells (Treppenhäuser): a head row with each group's label over its `span` columns. */
  columnGroups?: { label: string; span: number }[];
  selected?: string | null;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  label: string;
  header?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const floors = rows.flatMap((r) => (r.kind === 'floor' ? [r.floor] : []));
  const live = floors.map((f) => f.cells.filter((c) => !c.dim && !c.gap));
  const first = live.flat()[0]?.id;
  const focusId = selected && live.some((cs) => cs.some((c) => c.id === selected)) ? selected : first;
  // look the field up by its data attribute (ids may hold dots or quotes, no selector escaping needed)
  const cellEl = (id: string) => [...(ref.current?.querySelectorAll<HTMLButtonElement>('[data-cell]') ?? [])].find((el) => el.dataset.cell === id);
  const focus = (id: string) => cellEl(id)?.focus();
  // keep the selected field in view when it is chosen from outside (detail panel ‹ ›, search)
  useEffect(() => {
    if (!selected) return;
    cellEl(selected)?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, fi: number, id: string) => {
    const row = live[fi]!;
    const i = row.findIndex((c) => c.id === id);
    const go = (target: string | undefined) => { if (target) { e.preventDefault(); focus(target); } };
    const vertical = (dir: 1 | -1) => {
      for (let f = fi + dir; f >= 0 && f < live.length; f += dir) {
        const r = live[f]!;
        if (r.length) return r[Math.min(i, r.length - 1)]!.id;
      }
      return undefined;
    };
    switch (e.key) {
      case 'ArrowRight': return go(row[i + 1]?.id ?? live.slice(fi + 1).find((r) => r.length)?.[0]?.id);
      case 'ArrowLeft': return go(row[i - 1]?.id ?? [...live.slice(0, fi)].reverse().find((r) => r.length)?.at(-1)?.id);
      case 'ArrowDown': return go(vertical(1));
      case 'ArrowUp': return go(vertical(-1));
      case 'Home': return go(row[0]?.id);
      case 'End': return go(row.at(-1)?.id);
      case 'Enter': e.preventDefault(); return onOpen(id);
      default: return undefined;
    }
  };
  let fi = -1;
  return (
    <div className="house-plan" ref={ref} style={{ ['--plan-cols' as string]: columns }} role="grid" aria-label={label}>
      {header}
      {columnGroups && columnGroups.length > 0 && (
        <div className="hp-row hp-groups" role="row">
          <span className="hp-floor" role="presentation" />
          <span className="hp-cells" role="presentation">
            {columnGroups.map((g, i) => <span key={`${g.label}-${i}`} className="hp-group" role="columnheader" style={{ gridColumn: `span ${g.span}` }}>{g.label}</span>)}
          </span>
        </div>
      )}
      {rows.map((r) => {
        if (r.kind === 'fold') {
          const f = r.fold;
          return (
            <div key={f.id} className="hp-row hp-fold" role="row">
              <span className="hp-floor" role="rowheader">{f.label}</span>
              <button type="button" className="hp-fold-btn" onClick={f.onExpand}>
                <span>{f.text}</span>
                <span className="hp-fold-cta">{f.expandLabel}</span>
              </button>
            </div>
          );
        }
        fi++;
        const idx = fi;
        const fl = r.floor;
        return (
          <div key={fl.id} className="hp-row" role="row">
            <span className="hp-floor" role="rowheader"><strong>{fl.label}</strong>{fl.hint && <span className="hp-hint">{fl.hint}</span>}</span>
            <span className="hp-cells" role="presentation">
              {fl.cells.map((c) => (c.gap ? <span key={c.id} className="hp-gap" aria-hidden /> : (
                <button
                  key={c.id}
                  type="button"
                  role="gridcell"
                  data-cell={c.id}
                  className={['hp-cell', `oc-${c.kind}`, c.band ? `b-${c.band}` : '', c.dim ? 'is-dim' : '', c.id === selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                  aria-label={c.name}
                  aria-selected={c.id === selected}
                  tabIndex={!c.dim && c.id === focusId ? 0 : -1}
                  disabled={c.dim}
                  title={c.name}
                  onClick={() => onSelect(c.id)}
                  onDoubleClick={() => onOpen(c.id)}
                  onKeyDown={(e) => onKey(e, idx, c.id)}
                >
                  <span className="hp-num">{c.label}</span>
                  {c.arrears && <span className="occ-dot" aria-hidden />}
                </button>
              )))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Legend of the status system (house drawing, plan): shape and colour, the same everywhere. */
export function OccupancyLegend({ items }: { items: { key: OccupancyKind | 'move_out_band' | 'move_in_band' | 'arrears'; label: ReactNode }[] }) {
  return (
    <span className="legend occ-legend">
      {items.map((i) => <span key={i.key} className="legend-item"><span className={`occ-sw sw-${i.key}`} aria-hidden />{i.label}</span>)}
    </span>
  );
}
